import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik, FormikProvider } from 'formik';
import { ReferralsApiRepository } from '@packages/web-shared/api';
import {
  AlertError,
  Button,
  Form,
  FormFieldset,
  FormikInputText,
  FormikTextArea,
  FormikDatepicker,
  FormikDropdownSearch,
  FormikPhoneInput,
  FormLabel,
  Checkbox,
  isFormikValid,
} from '@percihealth/react';
import {
  MAX_REFERRAL_DOCUMENTS,
  CoverageRole,
  HealixGender,
  OrganizationId,
  ReferralCreateHealixSchema,
  ReferralCreateHealix,
  ReferralConsentEvidence,
  ReferralConsentStatus,
  ReferralDocumentType,
  ReferralStoredDocument,
  ReferralStoredDocuments,
  ReferralType,
  displayCoverageRole,
  filterReferralDocumentsOfType,
  findReferralDocumentOfType,
  generateFirebaseId,
  allJoinReasons,
} from '@packages/core-shared';
import { zodValidate } from '@packages/web-shared';
import { useFetchReferralFormSettings } from '../../../../useFetchReferralFormSettings';
import {
  DocumentUpload,
  ReferralInternalNotesField,
} from '@packages/web-shared/components/referrals';
import TrashIcon from '@packages/web-shared/components/icons/TrashIcon';

const joinReasonOptions = allJoinReasons.map((cr) => ({
  value: cr.id,
  name: cr.value,
}));

// The optional dropdowns open on nothing chosen — "Not selected" is the way
// back to that, not a value the referral carries (an empty choice is dropped
// from the body, see toReferralBody)
const NOT_SELECTED = { value: '', name: 'Not selected' };

const genderOptions = [
  NOT_SELECTED,
  ...Object.values(HealixGender).map((gender) => ({
    value: gender,
    name: gender,
  })),
];

const coverageRoleOptions = [
  NOT_SELECTED,
  ...Object.values(CoverageRole).map((role) => ({
    value: role,
    name: displayCoverageRole(role),
  })),
];

// What the form holds while the referrer types. The Healix fields stay the
// RAW strings a Healix body carries — `diagnosis` above all — and the
// referral schema parses them, exactly as it does for a referral posted to
// the API (ReferralHealixDiagnosisSchema). Nothing is parsed here, so the
// page exercises the same code the integration does.
type HealixReferralFormValues = Omit<
  ReferralCreateHealix,
  'patient' | 'coverage' | 'diagnosis'
> & {
  patient: Omit<ReferralCreateHealix['patient'], 'gender' | 'dob'> & {
    gender: string;
    dob: Date | null;
  };
  coverage: { role: string; subscriberId: string };
  diagnosis: { conditions: string; symptoms: string };
};

// A field the referrer left blank is not sent at all: an empty postcode or
// policy number would otherwise be stored as an empty string, and an empty
// gender or coverage role would fail the schema, which only knows its own
// values.
const filled = (value?: string) => value?.trim() || undefined;

// The body the referral is validated AND posted as, so the form validates
// exactly what it sends.
const toReferralBody = (values: HealixReferralFormValues) => {
  const { patient, coverage, diagnosis, documents } = values;

  return {
    ...values,
    patient: {
      ...patient,
      phone: filled(patient.phone),
      gender: filled(patient.gender) as HealixGender | undefined,
      dob: patient.dob ?? undefined,
      address: filled(patient.address),
      postcode: filled(patient.postcode),
      externalId: filled(patient.externalId),
      policyNumber: filled(patient.policyNumber),
      claimNumber: filled(patient.claimNumber),
      client: filled(patient.client),
    },
    // `coverage` is optional as a whole, but its role is required inside it:
    // no role chosen, no coverage sent
    coverage: filled(coverage.role)
      ? {
          role: coverage.role as CoverageRole,
          subscriberId: filled(coverage.subscriberId),
        }
      : undefined,
    diagnosis:
      filled(diagnosis.conditions) || filled(diagnosis.symptoms)
        ? {
            conditions: filled(diagnosis.conditions),
            symptoms: filled(diagnosis.symptoms),
          }
        : undefined,
    // an empty array would be stored as an empty documents list
    documents: documents?.length ? documents : undefined,
  };
};

const validateReferral = zodValidate(ReferralCreateHealixSchema);

const validateReferralForm = (values: HealixReferralFormValues) => {
  try {
    return validateReferral(toReferralBody(values));
  } catch (err) {
    // A diagnosis string the parser cannot read at all THROWS instead of
    // failing a field (ReferralHealixDiagnosisSchema). The API reports it as
    // the validation error it is (parseRequest) and so does the form, on the
    // field the message names.
    const message = (err as Error).message;

    // ...and the rest of the form is validated without it, so the referrer
    // still sees every other field that needs fixing
    return {
      ...validateReferral({ ...toReferralBody(values), diagnosis: undefined }),
      diagnosis: message.startsWith('Diagnosis symptoms')
        ? { symptoms: message }
        : { conditions: message },
    };
  }
};

export default function ReferralForm() {
  const referralsApiRepository = useMemo(
    () => new ReferralsApiRepository(),
    [],
  );

  const navigate = useNavigate();
  const [responseError, setResponseError] = useState<string | undefined>(
    undefined,
  );

  // The referral ID is minted here, before the referral exists: a document is
  // uploaded to "referrals/{referralId}/…" and the API only accepts a path
  // from the referral's own folder. Generated once per mounted form
  const [referralId] = useState(generateFirebaseId());

  // The two consent questions of this form are independent to the referrer but
  // they answer ONE thing — how we know the patient consented to being referred
  // — so they are kept apart here and combined into a single evidence below.
  const [consentAttested, setConsentAttested] = useState(false);
  const [consentPhoneCall, setConsentPhoneCall] = useState(false);

  // Which optional fields this organization's form displays. The API accepts
  // the fields regardless — the settings drive the UI only.
  const { settings: referralFormSettings } = useFetchReferralFormSettings(
    OrganizationId.healix,
  );

  const referralHandleOnsubmit = async (values: any, actions: any) => {
    if (!isFormikValid(formik)) {
      return;
    }

    try {
      await referralsApiRepository.create({
        ...toReferralBody(values),
        type: ReferralType.healix,
      });

      navigate('/referred');
    } catch (error) {
      console.error(error);
      setResponseError((error as Error).message);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const formik = useFormik({
    // enableReinitialize: true,
    initialValues: {
      id: referralId,
      documents: [],
      patient: {
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        gender: '',
        dob: null,
        address: '',
        postcode: '',
        externalId: '',
        policyNumber: '',
        claimNumber: '',
        client: '',
      },
      coverage: {
        role: '',
        subscriberId: '',
      },
      diagnosis: {
        conditions: '',
        symptoms: '',
      },
      referrer: {
        email: '',
      },
      details: '',
      notes: {
        internal: '',
      },
      joinReasonId: allJoinReasons[0].id,
      consent: undefined,
    } as HealixReferralFormValues,
    validate: validateReferralForm,
    onSubmit: referralHandleOnsubmit,
  });

  // A telephone call is the stronger evidence — we ask the patient ourselves,
  // so their answer, not the referrer's word, decides the referral. Until the
  // referrer attests nothing is sent, and the create schema then blocks the
  // submission (requiresPatientConsentEvidence).
  const applyConsent = (attested: boolean, phoneCall: boolean) => {
    const evidence = !attested
      ? undefined
      : phoneCall
        ? ReferralConsentEvidence.phoneCall
        : ReferralConsentEvidence.referrerAttested;

    formik.setFieldValue(
      'consent',
      evidence
        ? {
            patient: {
              referral: {
                evidence,
                // where the consent stands, stated by the form rather than
                // inferred from the evidence: a telephone call is ours still to
                // make, the referrer's confirmation is consent in hand
                status: phoneCall
                  ? ReferralConsentStatus.pending
                  : ReferralConsentStatus.given,
              },
            },
          }
        : undefined,
      true /*validate*/,
    );
  };

  // The documents are one typed array (PPL-3549): each section below reads its
  // own type out of it and writes the whole array back
  const documents = formik.values.documents ?? [];
  const clinicalReport = findReferralDocumentOfType(
    documents,
    ReferralDocumentType.clinicalReport,
  );
  const patientConsent = findReferralDocumentOfType(
    documents,
    ReferralDocumentType.patientReferralConsent,
  );
  const supportingDocuments = filterReferralDocumentsOfType(
    documents,
    ReferralDocumentType.supporting,
  );

  const setDocuments = (updated: ReferralStoredDocuments) =>
    formik.setFieldValue('documents', updated, true /*validate*/);

  // A referral holds one clinical report and one patient consent, so uploading
  // again replaces the one it has
  const replaceDocument = (uploaded: ReferralStoredDocuments) =>
    setDocuments([
      ...documents.filter(
        (doc) => !uploaded.some((up) => up.type === doc.type),
      ),
      ...uploaded,
    ]);

  const addDocuments = (uploaded: ReferralStoredDocuments) =>
    setDocuments([...documents, ...uploaded]);

  const removeDocument = (document: ReferralStoredDocument) =>
    setDocuments(documents.filter((doc) => doc.path !== document.path));

  const uploadedDocument = (document: ReferralStoredDocument) => (
    <div
      style={{ marginTop: '8px', display: 'flex', gap: '16px' }}
      key={document.path}
    >
      <div style={{ wordBreak: 'break-word' }}>{document.name}</div>
      <div
        style={{
          cursor: 'pointer',
          color: 'var(--global-color-error)',
        }}
        onClick={() => removeDocument(document)}
      >
        <TrashIcon />
      </div>
    </div>
  );

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <FormFieldset title="Patient's details">
          <FormikInputText
            name="patient.firstname"
            required
            label="Patient first name"
            value={formik.values.patient.firstname}
          />
          <FormikInputText
            name="patient.lastname"
            required
            label="Patient surname"
            value={formik.values.patient.lastname}
          />
          <FormikInputText
            name="patient.email"
            required
            label="Patient email address"
            value={formik.values.patient.email}
            onBlur={(e) => {
              formik.setFieldValue(
                'patient.email',
                e.target.value?.toLowerCase(),
              );
              formik.setFieldTouched(
                'patient.email',
                true /* set touched=true */,
                false /* do not validate, validation is set onChange */,
              );
            }}
          />
          <FormikPhoneInput
            name="patient.phone"
            label="Patient telephone number"
            // we cannot ask for consent by telephone without a phone number
            // (enforced by the referral schema)
            required={consentPhoneCall}
            value={formik.values.patient.phone}
          />
          <FormikDropdownSearch
            name="patient.gender"
            label="Patient gender"
            value={formik.values.patient.gender}
            options={genderOptions}
          />
          <FormikDatepicker
            name="patient.dob"
            label="Patient date of birth"
            value={formik.values.patient.dob}
          />
          <FormikInputText
            name="patient.address"
            label="Patient address"
            placeholder="23 Street, City"
            value={formik.values.patient.address ?? ''}
          />
          <FormikInputText
            name="patient.postcode"
            label="Patient postcode"
            placeholder="AB1 2CD"
            value={formik.values.patient.postcode ?? ''}
          />
          {referralFormSettings?.showPatientConsentPhoneCall && (
            <Checkbox
              id="consentPatientPhoneCall"
              value={consentPhoneCall.toString()}
              checked={consentPhoneCall}
              text="The member would like to receive a telephone call before creating their account"
              onChange={(e) => {
                setConsentPhoneCall(e.target.checked);
                applyConsent(consentAttested, e.target.checked);
              }}
            />
          )}
          <FormikDropdownSearch
            label="Join reason"
            required
            name="joinReasonId"
            onChange={(val) =>
              formik.setFieldValue(
                'joinReasonId',
                Number(val),
                true /*validate*/,
              )
            }
            value={formik.values.joinReasonId.toString()}
            options={joinReasonOptions}
          />
        </FormFieldset>
        {/* The fields sit two to a row and each one's input follows its own
            label, so a label that wraps would push its input below its
            neighbour's. What a field means is said in the label and shown as
            the placeholder rather than in a line of guidance under the label,
            which at this column width (from 420px) would wrap and do exactly
            that — the guidance the fields DO carry is on the full-width
            diagnosis boxes below, where nothing sits beside them. */}
        <FormFieldset title="Policy details">
          <FormikInputText
            name="patient.externalId"
            label="Patient member ID"
            placeholder="ABC_123"
            value={formik.values.patient.externalId ?? ''}
          />
          <FormikInputText
            name="patient.policyNumber"
            label="Policy number"
            placeholder="POL-123"
            value={formik.values.patient.policyNumber ?? ''}
          />
          <FormikInputText
            name="patient.claimNumber"
            label="Claim number"
            placeholder="CLM-2026-048291"
            value={formik.values.patient.claimNumber ?? ''}
          />
          <FormikInputText
            name="patient.client"
            label="Client (employer or scheme)"
            placeholder="Example employer scheme"
            value={formik.values.patient.client ?? ''}
          />
          <FormikDropdownSearch
            name="coverage.role"
            label="Coverage role"
            value={formik.values.coverage.role}
            options={coverageRoleOptions}
          />
          <FormikInputText
            name="coverage.subscriberId"
            label="Principal member number (dependant)"
            placeholder="EMPLOYEE_123"
            value={formik.values.coverage.subscriberId}
          />
        </FormFieldset>
        <FormFieldset title="Your details">
          <FormikInputText
            name="referrer.email"
            required
            label="Referrer email address"
            value={formik.values.referrer.email}
            onBlur={(e) => {
              formik.setFieldValue(
                'referrer.email',
                e.target.value?.toLowerCase(),
              );
              formik.setFieldTouched(
                'referrer.email',
                true /* set touched=true */,
                false /* do not validate, validation is set onChange */,
              );
            }}
          />
        </FormFieldset>
        <FormFieldset title="Diagnosis">
          {/* Both are sent as typed and parsed by the referral schema, in the
              raw form the Healix API takes them (PPL-3544, PPL-3548) */}
          <FormikTextArea
            name="diagnosis.conditions"
            className="full-width"
            label="Diagnosis conditions"
            description='Separated by a semicolon, each one as "code - CONDITION NAME"'
            placeholder="00410 - SHIGELLA FLEXNERI; 00730 - INTEST TRICHOMONIASIS"
            rows={2}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.diagnosis.conditions}
          />
          <FormikTextArea
            name="diagnosis.symptoms"
            className="full-width"
            label="Diagnosis symptoms"
            description="Free text, separated by a semicolon or a comma"
            placeholder="Persistent cough; Unexplained weight loss"
            rows={2}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.diagnosis.symptoms}
          />
        </FormFieldset>
        <FormFieldset title="Details of referral">
          <FormikTextArea
            name="details"
            className="full-width"
            required
            label="Please provide details of the referral, this will be sent to the
              Perci Health CNS"
            rows={5}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.details}
          />
          {/* the details above reach the CNS; these notes reach nobody outside
              Perci */}
          <ReferralInternalNotesField />
        </FormFieldset>

        <FormFieldset title="Documents">
          <div>
            <FormLabel>Clinical report (PDF, Microsoft Word, Images)</FormLabel>
            {clinicalReport && (
              <div style={{ marginLeft: '8px', marginBottom: '24px' }}>
                {uploadedDocument(clinicalReport)}
              </div>
            )}
            <DocumentUpload
              type={ReferralDocumentType.clinicalReport}
              extensions=".pdf, .doc, .docx, .jpg, .jpeg, .png"
              referralId={referralId}
              onUploaded={replaceDocument}
            />
          </div>
          <div>
            <FormLabel>
              Patient consent to referral (PDF, Microsoft Word, Images)
            </FormLabel>
            {patientConsent && (
              <div style={{ marginLeft: '8px', marginBottom: '24px' }}>
                {uploadedDocument(patientConsent)}
              </div>
            )}
            <DocumentUpload
              type={ReferralDocumentType.patientReferralConsent}
              extensions=".pdf, .doc, .docx, .jpg, .jpeg, .png"
              referralId={referralId}
              onUploaded={replaceDocument}
            />
          </div>
          <div className="full-width">
            <FormLabel>
              Supporting documents (PDF, Microsoft Word, Images)
            </FormLabel>
            {supportingDocuments.length > 0 && (
              <div style={{ marginLeft: '8px', marginBottom: '24px' }}>
                {supportingDocuments.map(uploadedDocument)}
              </div>
            )}
            <DocumentUpload
              type={ReferralDocumentType.supporting}
              extensions=".pdf, .doc, .docx, .jpg, .jpeg, .png"
              referralId={referralId}
              slotsLeft={MAX_REFERRAL_DOCUMENTS - documents.length}
              onUploaded={addDocuments}
            />
          </div>
        </FormFieldset>

        <FormFieldset title="">
          <div className="full-width"></div>
          {/* the referrer attesting to the patient's consent (was `acceptForm`) */}
          <Checkbox
            id="consentReferrerAttested"
            required
            value={consentAttested.toString()}
            checked={consentAttested}
            text="I confirm I have consent from the patient to refer them to Perci Health"
            error={
              formik.submitCount > 0 && !consentAttested
                ? 'Please confirm having the consent'
                : undefined
            }
            onChange={(e) => {
              setConsentAttested(e.target.checked);
              applyConsent(e.target.checked, consentPhoneCall);
            }}
          />
          <div>
            <Button
              type="submit"
              level="cta"
              size="large"
              submitting={formik.isSubmitting}
            >
              Submit referral
            </Button>
          </div>
          <div className="full-width">
            {responseError && <AlertError>{responseError}</AlertError>}
          </div>
        </FormFieldset>
      </Form>
    </FormikProvider>
  );
}
